import { Cell, NonEditableCell } from '@silevis/reactgrid';
import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import addCell from '../helpers/addCell';
import usePlanStore, {
  Week,
} from '../../../../../../../../../../../store/planStore';
import usePlanTotals from './usePlanTotals';
import getPercentFromValue from '../helpers/getPercentFromValue';
import normalizeByNumberOfDays from '../helpers/normalizeByNumberOfDays';

const useAddCopyDataRows = (cells: Cell[]) => {
  const { countryForms, selectedCountry } = usePlanStore();
  const tvConfig = countryForms[selectedCountry!]?.offline?.tv;
  const weeks = tvConfig?.grpTable?.weeks ?? [];

  const theme = useTheme();

  const stableRows = 3;
  const stableColumns = 2;

  const { planTotals } = usePlanTotals(weeks);

  const memoizedWeeks = useMemo(() => weeks, [weeks]);

  const tableData = memoizedWeeks.map((week) => week.copy.value);

  tableData[0]?.forEach((copy, rowIndex) => {
    addCell({
      cells,
      rowIndex: rowIndex + stableRows,
      colIndex: 0,
      Template: NonEditableCell,
      value: copy.name,
      sx: {
        color: theme.palette.grpTable.text.main,
        fontWeight: 500,
        fontSize: '12px',
        background: theme.palette.grpTable.background.default,
        textAlign: 'center',
      },
    });
    addCell({
      cells,
      rowIndex: rowIndex + stableRows,
      colIndex: 1,
      Template: NonEditableCell,
      value: +(copy.name?.match(/^(\d+)/)?.[0] ?? 0),
      sx: {
        color: theme.palette.grpTable.text.main,
        fontWeight: 700,
        fontSize: '14px',
        background: theme.palette.grpTable.background.default,
      },
    });

    const getWeekNumberOfDays = (
      lastWeek: number,
      nextWeek: number,
      week: Week,
      weekIndex: number
    ) => {
      if (lastWeek === week.weekNumber)
        return week.numberOfDays + weeks[weekIndex - 1].numberOfDays;
      if (nextWeek === week.weekNumber)
        return week.numberOfDays + weeks[weekIndex + 1].numberOfDays;
      return week.numberOfDays;
    };

    const newCells = memoizedWeeks.map((week, weekIndex) => {
      const cellValue = tableData[weekIndex][rowIndex]?.value;
      const lastWeek = weeks[weekIndex - 1]?.weekNumber;
      const nextWeek = weeks[weekIndex + 1]?.weekNumber;
      return {
        rowIndex: rowIndex + stableRows,
        colIndex: weekIndex + stableColumns,
        Template: NonEditableCell,
        props: {
          value: getPercentFromValue(
            cellValue,
            normalizeByNumberOfDays(
              tvConfig?.weeklyWeight?.current,
              getWeekNumberOfDays(lastWeek, nextWeek, week, weekIndex)
            )
          ),
          style: {
            color: theme.palette.grpTable.text.main,
            fontWeight: 700,
            fontSize: '14px',
            background: theme.palette.grpTable.background.nonEditable,
          },
        },
      };
    });

    cells.push(...newCells);

    const consecutiveNumberOfDays = weeks.reduce(
      (acc, week) => acc + week.numberOfDays,
      0
    );

    let separatedWeeksLength = 0;
    let lastWeekNumber = 0;
    weeks.forEach((week) => {
      if (lastWeekNumber !== week.weekNumber) {
        separatedWeeksLength++;
        lastWeekNumber = week.weekNumber;
      }
    });

    addCell({
      cells,
      rowIndex: rowIndex + stableRows,
      colIndex: weeks.length + stableColumns,
      Template: NonEditableCell,
      value: getPercentFromValue(
        planTotals[rowIndex],
        normalizeByNumberOfDays(
          tvConfig?.weeklyWeight?.current ?? 0,
          undefined,
          consecutiveNumberOfDays,
          separatedWeeksLength
        )
      ),
      sx: {
        color: theme.palette.grpTable.text.light,
        fontWeight: 700,
        fontSize: '14px',
        background: theme.palette.grpTable.background.default,
      },
    });
  });
};

export default useAddCopyDataRows;
